import {
    RaikoBaseContainer,
    RaikoGridOneColumn,
    RaikoTableTitleContainer,
    RaikoBreadcrumb,
    raikoBreadCrumbItemGenerator,
    RaikoBaseCardContainer,
    RaikoTableSearchContainer,
    RaikoNumberInputOutlined,
    RaikoMaterialOutlinedInput,
    RaikoDatePicker,
    convertPersianDateToUniversalDate,
    showErrorMessage,
    RaikoCustomTable,
    convertCurrencyFormatPositiveNegative,
    convertUniversalDateTimeToPersianDateTime
} from "@moeindal/base-raiko-panel-components";
import {useNavigate, useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {useDebounce} from "@uidotdev/usehooks";
import {useAuthState} from "../../context/AuthContext";
import {LoanService} from "../../serverService/loan/LoanService";
import {addFiltersToUrl} from "../../util/FiltersHelper";
import {RouteHelper} from "../../route/RouteHelper";
import {css} from "@emotion/css";
import {stringIsEmpty} from "../../util/VariableChecker";
import {detectServerErrorMessage} from "../../util/detectServerErrorMessage";
import RaikoShowPrice from "../../raikoComponents/raikoShowPrice/RaikoShowPrice";

export default function LoansList() {

    const [searchParam, setSearchParam] = useSearchParams();
    const [page, setPage] = useState(searchParam.get("page") || 1);
    const size = 20

    const navigate = useNavigate()

    const [nationalCode, setNationalCode] = useState(searchParam.get("national-code") || "");
    const [groupName, setGroupName] = useState(searchParam.get("group-name") || "");
    const [groupCode, setGroupCode] = useState(searchParam.get("group-code") || "")
    const [accountCode, setAccountCode] = useState(searchParam.get("account-code") || "");
    const [startDate, setStartDate] = useState(searchParam.get("start-date") || "")
    const [endDate, setEndDate] = useState(searchParam.get("end-date") || "")


    const filterNationalCode = useDebounce(nationalCode, 300)
    const filterGroupName = useDebounce(groupName, 300)
    const filterGroupCode = useDebounce(groupCode, 300)
    const filterAccountCode = useDebounce(accountCode, 300)
    const filterStartDate = useDebounce(startDate, 300)
    const filterEndDate = useDebounce(endDate, 300)

    const [tableLoading, setTableLoading] = useState(true)
    const [loanItems, setLoanItems] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const {fundID} = useAuthState()

    const service = new LoanService()

    useEffect(() => {
        if (
            typeof filterGroupName !== "undefined" &&
            typeof filterGroupCode !== 'undefined' &&
            typeof filterNationalCode !== 'undefined' &&
            typeof filterStartDate !== 'undefined' &&
            typeof filterEndDate !== 'undefined' &&
            typeof filterAccountCode !== 'undefined'
        ) {
            getDataFromServer()
            prepareFiltersParam()
        }
    }, [
        filterGroupName,
        filterGroupCode,
        filterNationalCode,
        filterAccountCode,
        filterStartDate,
        filterEndDate,
        page,
        fundID
    ]);

    function getDataFromServer() {
        setTableLoading(true)
        service.getLoans(
            fundID,
            page - 1,
            size,
            filterNationalCode,
            filterGroupCode,
            filterGroupName,
            filterAccountCode,
            stringIsEmpty(filterStartDate) ? null : convertPersianDateToUniversalDate(filterStartDate),
            stringIsEmpty(filterEndDate) ? null : convertPersianDateToUniversalDate(filterEndDate),
        ).then(response => {
            setTotalItems(response.numberOfItems)
            setPageCount(response.totalPage)

            setLoanItems([])
            response.data.map((item, index) => {
                setLoanItems(prev => [
                    ...prev,
                    prepareLoanItem(
                        item.id,
                        ((page - 1) * size) + index + 1,
                        item.balance,
                        item.amountOfLoan,
                        item.createdAt,
                        item.createdBy,
                        item.depositAccountNumber,
                        item.firstName,
                        item.lastName,
                        item.grantDate,
                        item.groupName,
                        item.nationalCode,
                        item.numberOfInstallments,
                        item.paymentMaturity,
                        item.remindAmount
                    )
                ])
            })

        }).catch(reason => {
            showErrorMessage(detectServerErrorMessage(reason))
        }).finally(_ => {
            setTableLoading(false)
        })
    }

    function prepareLoanItem(
        id,
        number,
        balance,
        amountOfLoan,
        createdAt,
        creator,
        accountNumber,
        firstName,
        lastName,
        grantDate,
        groupName,
        nationalCode,
        numberOfInstallments,
        paymentMaturity,
        remindAmount) {

        return {
            id,
            number,
            loanId: id,
            fullName: `${firstName} ${lastName}`,
            accountNumber,
            nationalCode,
            creator,
            groupName,
            balance: <RaikoShowPrice amount={balance} centerTextAlign/>,
            amountOfLoan: convertCurrencyFormatPositiveNegative(amountOfLoan),
            remindAmount: convertCurrencyFormatPositiveNegative(remindAmount),
            numberOfInstallments,
            grantDate: convertUniversalDateTimeToPersianDateTime(grantDate),
            createdAt: convertUniversalDateTimeToPersianDateTime(createdAt)

        }
    }

    function prepareFiltersParam() {
        const filters = []

        if (page && page > 1)
            filters.push(`page=${page}`)

        if (!stringIsEmpty(filterGroupName))
            filters.push(`group-name=${filterGroupName}`)

        if (!stringIsEmpty(filterGroupCode))
            filters.push(`group-code=${groupCode}`)

        if (!stringIsEmpty(filterNationalCode))
            filters.push(`national-code=${nationalCode}`)

        if (!stringIsEmpty(filterAccountCode))
            filters.push(`account-code=${accountCode}`)

        if (!stringIsEmpty(filterStartDate))
            filters.push(`start-date=${filterStartDate}`)

        if (!stringIsEmpty(filterEndDate))
            filters.push(`end-date=${filterEndDate}`)

        navigate(addFiltersToUrl(filters))
    }


    function handleChangePagination(newPage) {
        setPage(newPage + 1)
    }


    function handleShowDetail(id) {
        let title = ""

        loanItems.map((item) => {
            if (item.id === id) {
                title = item.fullName
            }
        })

        navigate(
            RouteHelper.LoanDetail.globalUrl.replace(":id", id),
            {state: {title: title}, replace: false}
        )
    }

    function handleDeleteAllFilters() {
        setGroupName("")
        setGroupCode("")
        setStartDate("")
        setEndDate("")
        setAccountCode("")
    }

    function handleShowCreateNewLoan() {
        navigate(
            RouteHelper.LoansCreateNewItem.globalUrl,
        )
    }

    return (
        <>
            <RaikoBaseContainer>
                <RaikoGridOneColumn className={css`gap: 1.5rem;`}>
                    <RaikoBreadcrumb breadCrumbItems={[
                        raikoBreadCrumbItemGenerator(RouteHelper.LoansList.name, ""),
                    ]}/>
                    <RaikoBaseCardContainer>
                        <RaikoGridOneColumn>
                            <RaikoTableTitleContainer
                                title={"وام ها"}
                                amount={totalItems}
                                hasCreateNewItem
                                createNewItemTitle={"وام جدید"}
                                handleCreateNewItem={handleShowCreateNewLoan}
                            />
                            <RaikoTableSearchContainer
                                hasFilter={true}
                                labelText={"کد ملی"}
                                placeHolder={"عبارت مورد نظر را وارد نمایید"}
                                setValue={setNationalCode}
                                value={nationalCode}
                                handleDeleteAllFilters={handleDeleteAllFilters}
                                filterStatus={
                                    !stringIsEmpty(filterGroupName) ||
                                    !stringIsEmpty(filterGroupCode) ||
                                    !stringIsEmpty(filterAccountCode) ||
                                    !stringIsEmpty(filterStartDate) ||
                                    !stringIsEmpty(filterEndDate)
                                }
                            >
                                <RaikoGridOneColumn>
                                    <RaikoMaterialOutlinedInput
                                        labelText={"نام گروه"}
                                        value={groupName}
                                        setValue={setGroupName}/>

                                    <RaikoNumberInputOutlined
                                        labelText={"کد گروه"}
                                        value={groupCode}
                                        setValue={setGroupCode}/>

                                    <RaikoNumberInputOutlined
                                        labelText={"شناسه حساب"}
                                        value={accountCode}
                                        setValue={setAccountCode}/>

                                    <RaikoDatePicker
                                        label={"تاریخ شروع"}
                                        date={startDate}
                                        setDate={setStartDate}/>

                                    <RaikoDatePicker
                                        label={"تاریخ پایان"}
                                        date={endDate}
                                        setDate={setEndDate}/>


                                </RaikoGridOneColumn>

                            </RaikoTableSearchContainer>
                            <RaikoCustomTable
                                header={header}
                                data={loanItems}
                                isLoading={tableLoading}
                                pageCount={pageCount}
                                newPage={page}
                                handlePaginationChange={handleChangePagination}
                                rowClicked={handleShowDetail}
                            />
                        </RaikoGridOneColumn>
                    </RaikoBaseCardContainer>
                </RaikoGridOneColumn>
            </RaikoBaseContainer>
        </>
    )
}


const header = [
    {
        id: "number",
        label: "#"
    },
    {
        id: "loanId",
        label: "شناسه وام"
    },
    {
        id: "accountNumber",
        label: "شناسه حساب"
    },
    {
        id: "fullName",
        label: "نام و نام خانوادگی"
    },
    {
        id: "nationalCode",
        label: "کد ملی"
    },
    {
        id: "groupName",
        label: "گروه"
    },
    {
        id: "balance",
        label: "موجودی (ریال)"
    },
    {
        id: "amountOfLoan",
        label: "مبلغ وام (ریال)"
    },
    {
        id: "remindAmount",
        label: "باقی مانده (ریال)"
    },
    {
        id: "numberOfInstallments",
        label: "تعداد اقساط"
    },
    {
        id: "grantDate",
        label: "تاریخ پرداخت"
    },
    {
        id: "creator",
        label: "سازنده"
    },
    {
        id: "createdAt",
        label: "تاریخ ثبت"
    },
]
